<template>
    <vx-modal v-model:visible="visible" size="lg" :padding="false">
        <page-select-group v-model="selectedGroups">
            <template v-slot:header> Add Target Groups </template>
            <template v-slot:subheader>
                Add multiple groups to <strong>{{ props.messageStandardName }}</strong>
            </template>
            <template v-slot:select-all-right>
                <div class="ml-auto">
                    <vx-dropdown v-if="groupCollections.length" placement="right">
                        <vx-button color="muted" :loading="loadingCollectionsMemberships > 0" size="xs">
                            Select by Collection <font-awesome-icon widthAuto :icon="faCaretDown"></font-awesome-icon>
                        </vx-button>
                        <template v-slot:items>
                            <vx-dropdown-item
                                v-for="groupCollection in groupCollections"
                                :key="groupCollection.id"
                                @click="selectGroupCollection(groupCollection)"
                                :data-test="`group-collection-${groupCollection.id}`"
                            >
                                {{ groupCollection.name }}
                            </vx-dropdown-item>
                        </template>
                    </vx-dropdown>
                </div>
            </template>
            <template v-slot:option="{ item }">
                <div class="flex justify-between items-center w-full">
                    <div>
                        <strong>{{ item.label || '-' }}</strong>
                        - {{ item.value }}
                    </div>

                    <vx-popover
                        v-if="willBeReassigned(item.value)"
                        hover
                        placement="top"
                        teleport
                        arrow
                        @open="onBadgeHover(item.value)"
                    >
                        <vx-badge color="info" size="xs">
                            Reassigned
                            <font-awesome-icon widthAuto :icon="faCircleInfo" />
                        </vx-badge>
                        <template v-slot:content>
                            <div class="w-72 max-w-full text-center">
                                <vx-icon-loading v-if="!getMessageStandardName(item.value)" spin />
                                <span v-else>
                                    Will be removed from
                                    <strong>{{ getMessageStandardName(item.value) }}</strong> and reassigned here.
                                </span>
                            </div>
                        </template>
                    </vx-popover>
                    <vx-popover
                        v-else-if="isAssigned(item.value)"
                        hover
                        placement="top"
                        teleport
                        arrow
                        @open="onBadgeHover(item.value)"
                    >
                        <vx-badge color="primary" size="xs">
                            Assigned
                            <font-awesome-icon widthAuto :icon="faCircleInfo" />
                        </vx-badge>
                        <template v-slot:content>
                            <div class="w-72 max-w-full text-center">
                                <span v-if="getGroupAssignment(item.value) === props.messageStandardId">
                                    Already assigned to this brand standard.
                                </span>
                                <vx-icon-loading v-else-if="!getMessageStandardName(item.value)" spin />
                                <span v-else>
                                    Already assigned to <strong>{{ getMessageStandardName(item.value) }}</strong
                                    >. If selected, will be removed and assigned here.
                                </span>
                            </div>
                        </template>
                    </vx-popover>
                </div>
            </template>
        </page-select-group>

        <template v-slot:footer>
            <div class="flex flex-col gap-4">
                <vx-alert v-if="reassignedGroups.length > 0" color="info" :closeable="false" class="mb-0">
                    <template v-slot:icon>
                        <font-awesome-icon :icon="faDiamondExclamation" />
                    </template>
                    <div class="flex justify-between items-center">
                        <span>
                            <strong>{{ pluralized('group', reassignedGroups.length, true) }}</strong> will be
                            reassigned</span
                        >
                        <vx-button @click="deselectReassignedGroups" size="sm" color="info" type="button">
                            Deselect Groups
                            <font-awesome-icon widthAuto :icon="faSquareCheck" />
                        </vx-button>
                    </div>
                </vx-alert>

                <div class="flex flex-col-reverse gap-4 md:flex-row">
                    <vx-button
                        @click="visible = false"
                        type="button"
                        data-test="cancel-button"
                        class="grow md:w-20 md:grow-0"
                        size="lg"
                        color="muted"
                    >
                        Cancel
                    </vx-button>
                    <vx-popover class="grow w-full" arrow placement="top" hover>
                        <vx-button
                            @click="submitAddGroups"
                            data-test="submit-button"
                            :loading="submitting"
                            :disabled="selectedGroups.length === 0"
                            type="button"
                            block
                            size="lg"
                            color="primary"
                        >
                            Add Groups <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                        <template v-if="selectedGroups.length === 0" v-slot:content>
                            You must select at least one Group
                        </template>
                    </vx-popover>
                </div>
            </div>
        </template>
    </vx-modal>
</template>

<script setup lang="ts">
import {
    faCaretDown,
    faCircleInfo,
    faCirclePlus,
    faDiamondExclamation,
    faSquareCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxIconLoading,
    VxModal,
    VxPopover,
} from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import { pluralized } from '~/components/filters';
import PageSelectGroup from '~/components/general/PageSelectGroup.vue';
import { useToasted } from '~/composables/useToasted';
import { groupCollectionsClient, messageStandardsClient } from '~/services';
import { catchRequestError } from '~/utils/request';
import type { GroupMappingType } from '~/services/messageStandards/messageStandardsClient.types';
import type { GroupCollection } from '~/services/groupCollections/groupCollectionsClient.types';

const teamId = Spark.state.currentTeam.id;

const props = defineProps<{
    messageStandardId: number;
    messageStandardName?: string;
    groupMappings: GroupMappingType[];
}>();

const emit = defineEmits<{
    added: [];
}>();

const visible = defineModel<boolean>('visible', { required: true });

const toasted = useToasted();
const selectedGroups = ref<{ value: string; label: string }[]>([]);
const submitting = ref(false);
const messageStandardNames = ref(new Map<number, string>());
const groupCollections = ref<GroupCollection[]>([]);
const loadingCollectionsMemberships = ref(0);
const collectionsMemberships: Record<number, string[]> = {};

const targetGroupsForMessageStandard = computed(() => {
    return props.groupMappings
        .filter((mapping) => mapping.custom_message_standard_id === props.messageStandardId)
        .map((mapping) => mapping.group_id);
});

const getGroupAssignment = (groupId: string) => {
    const mapping = props.groupMappings.find((m) => m.group_id === groupId);
    return mapping?.custom_message_standard_id;
};

const isAssigned = (groupId: string) => {
    return Boolean(getGroupAssignment(groupId));
};

const willBeReassigned = (groupId: string) => {
    const assignedTo = getGroupAssignment(groupId);
    return isGroupSelected(groupId) && assignedTo && assignedTo != props.messageStandardId;
};

const isGroupSelected = (groupId: string) => {
    return selectedGroups.value.some((g) => g.value === groupId);
};

const reassignedGroups = computed(() => {
    return selectedGroups.value.filter((selectedGroup) => willBeReassigned(selectedGroup.value));
});

const deselectReassignedGroups = () => {
    selectedGroups.value = selectedGroups.value.filter((selectedGroup) => !willBeReassigned(selectedGroup.value));
};

const fetchMessageStandardName = async (messageStandardId: number) => {
    if (messageStandardNames.value.has(messageStandardId)) {
        return;
    }

    try {
        const response = await messageStandardsClient.getMessageStandard({ teamId }, messageStandardId);
        messageStandardNames.value.set(messageStandardId, response.data.name || 'Unknown');
    } catch {
        messageStandardNames.value.set(messageStandardId, 'Unknown');
    }
};

const getMessageStandardName = (groupId: string) => {
    const messageStandardId = getGroupAssignment(groupId);
    if (!messageStandardId) {
        return undefined;
    }
    return messageStandardNames.value.get(messageStandardId);
};

const onBadgeHover = (groupId: string) => {
    const messageStandardId = getGroupAssignment(groupId);
    if (messageStandardId && messageStandardId !== props.messageStandardId) {
        fetchMessageStandardName(messageStandardId);
    }
};

const loadGroupCollections = async () => {
    try {
        const response = await groupCollectionsClient.getGroupCollections({ teamId });
        groupCollections.value = response.data.data;
    } catch {
        groupCollections.value = [];
    }
};

const selectCollectionMembership = (groupCollectionId: number) => {
    const groupIds = collectionsMemberships[groupCollectionId];

    if (!groupIds) {
        return;
    }

    const groupsToAdd = groupIds
        .map((groupId) => {
            return {
                value: groupId,
                label: groupId,
            };
        })
        .filter((group) => !selectedGroups.value.find((selectedGroup) => selectedGroup.value === group.value));

    selectedGroups.value = [...selectedGroups.value, ...groupsToAdd];
};

const selectGroupCollection = async (groupCollection: GroupCollection) => {
    if (collectionsMemberships[groupCollection.id]) {
        selectCollectionMembership(groupCollection.id);
        return;
    }

    loadingCollectionsMemberships.value++;
    collectionsMemberships[groupCollection.id] = [];

    try {
        const response = await groupCollectionsClient.getGroupCollectionGroupMemberships(
            { teamId },
            groupCollection.id
        );

        loadingCollectionsMemberships.value--;
        collectionsMemberships[groupCollection.id] = response.data.memberships ?? [];
        selectCollectionMembership(groupCollection.id);
    } catch (e) {
        loadingCollectionsMemberships.value--;
        catchRequestError(e);
        toasted.error(`It was not possible to select the groups from the collection "${groupCollection.name}"`);
    }
};

const submitAddGroups = async () => {
    if (submitting.value) {
        return;
    }

    if (!selectedGroups.value.length) {
        toasted.error('Select at least one group to add.');
        return;
    }

    try {
        submitting.value = true;

        const existingGroupIds = targetGroupsForMessageStandard.value;

        const allGroupIds = new Set([
            ...existingGroupIds,
            ...selectedGroups.value.map((selectedGroup) => selectedGroup.value),
        ]);

        await messageStandardsClient.updateTargetGroups({ teamId }, Number(props.messageStandardId), {
            group_ids: Array.from(allGroupIds),
        });

        const newGroupIds = selectedGroups.value.filter((selectedGroup) => !isAssigned(selectedGroup.value));
        const reassignedCount = reassignedGroups.value.length;

        if (newGroupIds.length || reassignedCount) {
            let title = '';
            let description: string | undefined;
            let type: 'success' | 'warning' = 'success';

            if (newGroupIds.length) {
                title = `${pluralized('target group', newGroupIds.length, true)} added`;
                if (reassignedCount) {
                    description = `${pluralized(
                        'target group',
                        reassignedCount,
                        true
                    )} removed from their previous brand standards and assigned here`;
                }
            } else {
                type = 'warning';
                title = `${pluralized('target group', reassignedCount, true)} reassigned`;
            }

            toasted.show({ title, description, type });
        }

        emit('added');
        visible.value = false;
        selectedGroups.value = [];
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        submitting.value = false;
    }
};

watch(visible, (isOpen) => {
    if (isOpen) {
        selectedGroups.value = [];
        loadGroupCollections();
    }
});
</script>
